import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    CircularProgress,
    IconButton,
} from '@mui/material';
import React, { useEffect, useState, useImperativeHandle, forwardRef, useCallback, useMemo, useRef } from 'react';
import { useSnackbar } from 'notistack';

import UploadDocumentModal from '../../../common/components/UploadDocumentModal';

import UploadDocumentsQuestionnaire, { type QuestionnaireAnswers } from './components/UploadDocumentsQuestionnaire';
import DocumentUploadList from './components/DocumentUploadList';
import useDocumentMetadataWrites from './hooks/useDocumentMetadataWrites';
import { type DocumentType } from './components/DocumentItem';
import {
    getDocumentTypesFromAnswers,
    getDocumentTypeDisplayName,
    getDocumentTypeDescription,
} from './utils/documentTypeMapping';

import CloseIcon from 'modules/common/components/Icons/CloseIcon';
import { useAccountingOnboardingProgress } from 'services/bookkeeping/queries/useAccountingOnboardingProgress';
import useUpdateOnboardingStep from 'services/bookkeeping/mutations/useUpdateOnboardingStep';
import useCompleteOnboardingStep from 'services/bookkeeping/mutations/useCompleteOnboardingStep';
import useDeleteMemberDocument from 'services/documents/mutations/useDeleteMemberDocument';
import ConfirmationDialog from 'modules/common/components/MUI/ConfirmationDialog';
import useMemberDocumentsByUuids from 'services/documents/queries/useMemberDocumentsByUuids';
import { useMember } from 'modules/common/queries/member';
import IncompleteStatementsDialog from 'modules/common/components/VerifiedStatementUpload/components/IncompleteStatementsDialog';
import useStatementUploadCompletion from 'modules/Onboarding/AccountingSetup/StatementUpload/hooks/useStatementUploadCompletion';
import WhyImportantLink from 'modules/Onboarding/AccountingSetup/components/WhyImportantLink';
import { ACCOUNTING_SETUP_HELP_CONTEXT } from 'modules/Onboarding/AccountingSetup/hooks/useOpenAccountingSetupHelp';
import { isSCorpMember } from 'utils/businessStructure';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';

interface UploadDocumentsProps {
    // StepWizard2 helpers
    helpers: {
        setCanProceed: (canProceed: boolean) => void;
        onNext?: () => void;
    };
    phaseRef: React.RefObject<{ isPhase1: boolean }> | null;
    /**
     * Whether the checklist asks for bank statements. False makes it business documents only:
     * Continue does not wait on statements and completing does not complete `statement_upload`.
     */
    // eslint-disable-next-line react/require-default-props
    includeStatements?: boolean;
}

export interface UploadDocumentsRef {
    handleContinue: () => Promise<void>;
    handleBack: () => void;
    isCompleting: boolean;
    /** A Phase 1 advance is in flight. Disables the footer without relabelling it. */
    isAdvancing: boolean;
    handleStepExit: () => Promise<boolean>;
}

const UploadDocuments = forwardRef<UploadDocumentsRef, UploadDocumentsProps>((props, ref) => {
    const { helpers, phaseRef, includeStatements = true } = props;
    const isRedesignV1 = useStatsigGate(FeatureGates.AccountingSetupRedesignV1);
    const {
        isEnabled: isStatementVerificationEnabled,
        isReady: areStatementsReady,
        outstandingAccounts,
        // Whether the statements section below will render upload cards, which is what makes the
        // header's "why do you need my bank statements" question answerable.
        hasUploadCards: hasStatementsToUpload,
        complete: completeWithStatements,
        incompleteDialogLabels,
    } = useStatementUploadCompletion({ enabled: includeStatements });

    const { setCanProceed, onNext } = helpers;
    const { sendTrackingEvent } = useEventTracking();

    // Use ref to store setCanProceed to avoid dependency issues
    const setCanProceedRef = React.useRef(setCanProceed);
    setCanProceedRef.current = setCanProceed;
    const { data: onboardingProgress } = useAccountingOnboardingProgress();
    const { data: member } = useMember();
    const { enqueueSnackbar } = useSnackbar();

    // Mutations for state management
    const updateStep = useUpdateOnboardingStep();
    const completeStep = useCompleteOnboardingStep();
    const deleteMemberDocumentMutation = useDeleteMemberDocument();

    const [showQuestionnaire, setShowQuestionnaire] = useState(true);
    const [questionnaireAnswers, setQuestionnaireAnswers] = useState<QuestionnaireAnswers>({
        individualTaxReturn: null,
        businessTaxReturn: null,
        businessVehicle: null,
        businessLoans: null,
        accountingSoftware: null,
    });

    // Track document upload section started -- once per mount
    const hasTrackedSectionStarted = useRef(false);
    useEffect(() => {
        if (hasTrackedSectionStarted.current) {
            return;
        }

        hasTrackedSectionStarted.current = true;
        sendTrackingEvent('Document Upload Section Started', {
            category: 'accounting_setup',
            action: 'section_started',
            stepName: 'upload_documents',
            userType: member?.usertype,
        });
    }, [sendTrackingEvent, member?.usertype]);

    // Define a type that can handle both native File objects and custom file metadata
    interface FileMetadata {
        id: string;
        file_name: string;
        name: string;
        file_format: string;
        created_at: string;
        year?: number;
        month?: number;
        quarter?: number;
    }

    const [uploadedDocuments, setUploadedDocuments] = useState<Record<string, (File | FileMetadata)[]>>({});
    const [skippedDocuments, setSkippedDocuments] = useState<Record<string, boolean>>({});
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<DocumentType | null>(null);
    const [isCompleting, setIsCompleting] = useState(false);
    const [isAdvancing, setIsAdvancing] = useState(false);

    /**
     * Re-entry guard for both writes, held on a ref rather than read off the state above.
     *
     * The footer is disabled while a write is in flight, but it learns that from a polled ref and
     * so lags by up to one tick; state would lag too, since a second click in the same tick reads
     * the pre-update value. Completing twice would dispatch the transaction-extraction fan-out
     * twice, which cannot be taken back.
     */
    const isWriteInFlightRef = useRef(false);
    const documentMetadata = useDocumentMetadataWrites(onboardingProgress?.other_document_upload_data);

    /**
     * The step is finished and the wizard has been told to advance.
     *
     * `onNext` is the wizard's `handleNext`, which calls `handleStepExit` straight back —
     * synchronously, from inside the write below, before its `finally` has cleared
     * `isWriteInFlightRef` and before any state update has committed. Read off state, that
     * re-entrant call would see a write still in flight and refuse the navigation it was
     * dispatched to allow; with the gate off it would reopen the very confirmation the member
     * just confirmed.
     */
    const hasConfirmedUploadRef = useRef(false);

    /**
     * The legacy pre-completion confirmation, shown only while the statement-verification gate is
     * off. Its Confirm calls the same `completeStepAndExit` the gated path calls directly, so the
     * two routes differ in what asks, never in what is written.
     */
    const [showCompleteConfirmModal, setShowCompleteConfirmModal] = useState(false);

    /** Downstream error rendered on the step. Cleared on each new attempt. */
    const [backendError, setBackendError] = useState<string | null>(null);

    /**
     * The member pressed Complete with months still outstanding, and is being asked once.
     *
     * This is the only place that question is now put. Ticking "I don't have these" on a card
     * used to raise it, which asked too early — the member was marking what they have, not
     * deciding to proceed — and asked once per card rather than once for the step.
     */
    const [isConfirmingIncomplete, setIsConfirmingIncomplete] = useState(false);

    // Note: We don't save questionnaire answers or skipped documents to backend
    // until the user is actually on the Upload Documents step
    // This prevents 500 errors when trying to update step data for a step the user isn't on

    // Get business accounts from onboarding progress.
    // Phase 1 decision (Slack, 2026-06-22): step 4 always asks for statements (the "safe pass") because
    // Quiltt account connection has proven flaky — we never suppress the manual ask based on a connection.
    // Connect-in-step-3 → suppress-here + delayed "Quiltt failed after X days" task is deferred to phase 2.
    const businessAccounts = useMemo(
        () => onboardingProgress?.bank_connection_data?.confirmed_business_accounts || [],
        [onboardingProgress?.bank_connection_data?.confirmed_business_accounts]
    );

    // Extract document UUIDs from onboarding progress metadata
    const documentUuids =
        onboardingProgress?.other_document_upload_data?.uploaded_documents?.map((doc) => doc.member_document_id) || [];

    // Query specific documents by UUIDs (efficient approach)
    const { data: memberDocumentsData } = useMemberDocumentsByUuids(documentUuids);

    // Track if we're currently loading data to prevent save loops
    // localStorage key for questionnaire answers
    const QUESTIONNAIRE_STORAGE_KEY = 'upload-documents-questionnaire-answers';

    // Load questionnaire answers from backend first, then fallback to localStorage
    useEffect(() => {
        // Priority 1: Load from backend if available
        if (onboardingProgress?.other_document_upload_data?.questionnaire_answers) {
            const backendAnswers = onboardingProgress.other_document_upload_data
                .questionnaire_answers as QuestionnaireAnswers;
            setQuestionnaireAnswers(backendAnswers);

            // Update localStorage to match backend data
            try {
                localStorage.setItem(QUESTIONNAIRE_STORAGE_KEY, JSON.stringify(backendAnswers));
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('❌ Failed to update localStorage with backend data:', error);
            }
        } else {
            // Priority 2: Fallback to localStorage if no backend data
            try {
                const storedAnswers = localStorage.getItem(QUESTIONNAIRE_STORAGE_KEY);
                if (storedAnswers) {
                    const parsedAnswers = JSON.parse(storedAnswers) as QuestionnaireAnswers;
                    setQuestionnaireAnswers(parsedAnswers);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('❌ Failed to load questionnaire answers from localStorage:', error);
            }
        }
    }, [onboardingProgress]); // Run when onboardingProgress changes

    // Load other data from backend when onboardingProgress is available
    useEffect(() => {
        if (onboardingProgress?.other_document_upload_data) {
            const uploadData = onboardingProgress.other_document_upload_data;

            // Load skipped documents from backend
            if (uploadData.skipped_documents) {
                setSkippedDocuments(uploadData.skipped_documents);
            }
        }
    }, [onboardingProgress]);

    // Load uploaded documents from MemberDocument collection when data is available
    useEffect(() => {
        if (memberDocumentsData?.documents && onboardingProgress?.other_document_upload_data) {
            const uploadData = onboardingProgress.other_document_upload_data;
            const uploadedDocs: Record<string, (File | FileMetadata)[]> = {};

            // Map MemberDocument records to our virtual document structure
            memberDocumentsData.documents.forEach((doc) => {
                // Find the corresponding metadata from onboarding progress
                const metadata = uploadData.uploaded_documents?.find((meta) => meta.member_document_id === doc.uuid);

                if (metadata) {
                    // Create a virtual document ID based on document type and index
                    // This matches the structure used in handleDocumentUpload
                    const documentType = doc.document_type;
                    const allDocuments = [
                        // Business documents
                        ...getDocumentTypesFromAnswers(questionnaireAnswers)
                            .filter((type) => type !== 'BUSINESS_BANK_STATEMENT')
                            .map((type, index) => ({
                                id: `business-doc-${index}`,
                                documentType: type,
                            })),
                        // Bank statement documents
                        ...businessAccounts.map((account) => ({
                            id: `bank-statement-${account.id}`,
                            documentType: 'BUSINESS_BANK_STATEMENT',
                            bankAccountId: account.id,
                            bankAccountName: `${account.name} (${account.institution_name})`,
                        })),
                    ];

                    // For bank statements, match by both document type AND bank account ID
                    let virtualDoc;
                    if (documentType === 'BUSINESS_BANK_STATEMENT') {
                        // Extract bank account ID from metadata
                        const bankAccountId = (metadata as any).bank_account_id;

                        virtualDoc = allDocuments.find(
                            (vDoc) =>
                                vDoc.documentType === documentType && vDoc.id === `bank-statement-${bankAccountId}`
                        );
                    } else {
                        // For other document types, match by document type only
                        virtualDoc = allDocuments.find((vDoc) => vDoc.documentType === documentType);
                    }

                    if (virtualDoc) {
                        const fileData = {
                            id: doc.uuid,
                            file_name: doc.file_name,
                            name: doc.file_name,
                            file_format: doc.file_format,
                            created_at: doc.created_at,
                            year: doc.year,
                            month: doc.month,
                            quarter: doc.quarter,
                        };

                        if (!uploadedDocs[virtualDoc.id]) {
                            uploadedDocs[virtualDoc.id] = [];
                        }
                        uploadedDocs[virtualDoc.id].push(fileData);
                    }
                }
            });

            setUploadedDocuments(uploadedDocs);
        }
    }, [memberDocumentsData, onboardingProgress, questionnaireAnswers, businessAccounts]);

    // Handle questionnaire answers change - save to localStorage immediately
    const handleAnswersChange = useCallback((answers: QuestionnaireAnswers) => {
        setQuestionnaireAnswers(answers);

        // Save to localStorage immediately (no backend calls)
        try {
            localStorage.setItem(QUESTIONNAIRE_STORAGE_KEY, JSON.stringify(answers));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('❌ Failed to save to localStorage:', error);
        }
    }, []);

    // Save questionnaire answers to backend when user clicks "Continue" to Phase 2
    const handleContinueToPhase2 = useCallback(async () => {
        if (isWriteInFlightRef.current) {
            return;
        }
        isWriteInFlightRef.current = true;
        setIsAdvancing(true);
        try {
            await updateStep.mutateAsync({
                step: 'other_document_upload',
                status: 'in_progress',
                data: {
                    questionnaire_answers: questionnaireAnswers,
                },
            });

            // Move to Phase 2
            setShowQuestionnaire(false);
            setCanProceed(false); // Reset to false until documents are uploaded/skipped

            // Update phase ref for footer
            if (phaseRef?.current) {
                Object.assign(phaseRef.current, { isPhase1: false });
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('❌ Backend save failed:', error);
            enqueueSnackbar('Failed to save questionnaire answers. Please try again.', {
                variant: 'error',
                autoHideDuration: 5000,
            });
        } finally {
            isWriteInFlightRef.current = false;
            setIsAdvancing(false);
        }
    }, [questionnaireAnswers, updateStep, enqueueSnackbar, setCanProceed, phaseRef]);

    // Note: We don't automatically cleanup documents when questionnaire answers change
    // This prevents accidental data loss and keeps the UX simple
    // Users can manually delete documents if needed

    /**
     * Finish the step: persist it, dispatch what completion triggers, and leave for the dashboard.
     *
     * Resolves `false` and surfaces `backendError` on the step rather than throwing, because both
     * callers have to know whether to keep the member here: the footer stops showing its loading
     * state, and `handleStepExit` has to refuse the navigation that asked for it.
     */
    const completeStepAndExit = useCallback(async (): Promise<boolean> => {
        if (isWriteInFlightRef.current) {
            // Not an error, and deliberately not `true`: the first attempt is still running, so a
            // caller asking whether it may navigate away has to be told no.
            return false;
        }
        isWriteInFlightRef.current = true;
        setIsCompleting(true);
        setBackendError(null);

        try {
            // The statement section renders inside this step, so this click is the only thing that
            // completes `statement_upload` — `useStatementStepWrites` only ever writes `in_progress`.
            // The hook owns when that transition may fire and what happens to waiver toasts around it.
            await completeWithStatements({
                before: async () => {
                    // After any metadata write still in flight, and with what it wrote.
                    const { uploaded_documents: uploadedDocumentsMetadata, document_types: documentTypes } =
                        await documentMetadata.settled();
                    await completeStep.mutateAsync({
                        step: 'other_document_upload',
                        data: {
                            questionnaire_answers: questionnaireAnswers,
                            uploaded_documents: uploadedDocumentsMetadata,
                            skipped_documents: Object.keys(skippedDocuments).filter((docId) => skippedDocuments[docId]),
                            document_types: documentTypes,
                            document_count: uploadedDocumentsMetadata.length,
                            completed_at: new Date().toISOString(),
                        },
                    });
                },
                after: () => {
                    sendTrackingEvent('Document Upload Section Completed', {
                        category: 'accounting_setup',
                        action: 'section_completed',
                        stepName: 'upload_documents',
                        documentCount: Object.values(uploadedDocuments).flat().length,
                        skippedDocuments: Object.keys(skippedDocuments).filter((docId) => skippedDocuments[docId])
                            .length,
                        documentTypes: Object.keys(uploadedDocuments),
                        includesStatements: includeStatements,
                    });

                    // Clear questionnaire answers from localStorage since the task is complete
                    try {
                        localStorage.removeItem(QUESTIONNAIRE_STORAGE_KEY);
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error('❌ Failed to clear questionnaire answers from localStorage:', error);
                    }

                    // No-op behind the gate, where nothing opened it.
                    setShowCompleteConfirmModal(false);
                    hasConfirmedUploadRef.current = true;
                    onNext?.();
                },
            });

            return true;
        } catch (error) {
            setBackendError(error instanceof Error ? error.message : 'Failed to complete document upload');
            return false;
        } finally {
            isWriteInFlightRef.current = false;
            setIsCompleting(false);
        }
    }, [
        completeWithStatements,
        questionnaireAnswers,
        documentMetadata,
        skippedDocuments,
        uploadedDocuments,
        completeStep,
        includeStatements,
        onNext,
        sendTrackingEvent,
    ]);

    /**
     * Finish the step, or put the question that has to be answered first on screen.
     *
     * Shared by every route that completes the step, so none of them can complete it without
     * asking what the others ask — stated twice, reviving one silently reintroduces a completion
     * with no prompt behind it. `false` means the member was asked rather than moved, which is
     * also the answer the wizard needs to hold them on the step.
     */
    const finishOrConfirm = useCallback(async (): Promise<boolean> => {
        // The one place the member is asked about months they are leaving behind. Reached whether
        // or not they waived: a waiver unlocks this button, it does not answer the question.
        if (isStatementVerificationEnabled && outstandingAccounts.length > 0) {
            setIsConfirmingIncomplete(true);
            return false;
        }
        // V1, and the gate-off legacy path, confirm before the same completion. The branch is on
        // what asks, never on what is written.
        if (isRedesignV1 || !isStatementVerificationEnabled) {
            setShowCompleteConfirmModal(true);
            return false;
        }
        return completeStepAndExit();
    }, [isRedesignV1, isStatementVerificationEnabled, outstandingAccounts, completeStepAndExit]);

    // Handle internal phase navigation (questionnaire <-> document list)
    const handleContinue = async () => {
        if (showQuestionnaire) {
            // Move from Phase 1 (questionnaire) to Phase 2 (document upload list)
            // Save to backend first, then move to Phase 2
            await handleContinueToPhase2();
            return;
        }
        // Phase 2 is the last phase, so Continue finishes the step.
        await finishOrConfirm();
    };

    /**
     * Proceed with the months outstanding. Under V1 that still goes through its completion
     * confirmation, as the direct path does; otherwise this is the completion the prompt held up.
     */
    const handleSubmitIncomplete = async () => {
        setIsConfirmingIncomplete(false);
        if (isRedesignV1) {
            setShowCompleteConfirmModal(true);
            return;
        }
        await completeStepAndExit();
    };

    /**
     * Handle step exit for StepWizard2 integration.
     *
     * Behind the gate, Phase 2 completes rather than blocking: the step has no further phase, so
     * navigating away from it is the member finishing it. The returned flag is the completion's own
     * result, so a failed write holds the member on the step with the error visible instead of
     * moving them off a step that was never finished.
     *
     * With the gate off this keeps its previous answer — open the confirmation, refuse the
     * navigation — so a member on the legacy path sees exactly what they saw before.
     *
     * Nothing reaches this today: the step supplies its own `footerRightElement`, which calls
     * `handleContinue` directly rather than going through the wizard's `proceedToNext`, and
     * `proceedToNext` is the only caller of `onStepExit` short of `handleSkipToReview`, which
     * needs a `skipToReviewPath` no Onboarding step sets. It shares `finishOrConfirm` with
     * `handleContinue` regardless, so reviving this route cannot reintroduce a completion with no
     * prompt behind it — only the preamble below is this route's own.
     */
    const handleStepExit = useCallback(async (): Promise<boolean> => {
        if (showQuestionnaire) {
            // Phase 1: Allow navigation (nothing to complete yet)
            return true;
        }
        if (hasConfirmedUploadRef.current) {
            // `onNext` calling back into the gate it was dispatched from. The step is already
            // written, so there is nothing left to ask and nothing left to write — anything but
            // `true` here strands the member on a step they finished.
            return true;
        }
        // Refusing the navigation is what puts the dialog on screen: the member is being asked,
        // not moved, which is exactly what `finishOrConfirm` returns false for.
        return finishOrConfirm();
    }, [showQuestionnaire, finishOrConfirm]);

    // Expose handleContinue, handleBack, and loading state to parent components
    const handleBack = () => {
        // Move from Phase 2 (document upload list) back to Phase 1 (questionnaire)
        setShowQuestionnaire(true);
        setCanProceed(false); // Reset to false until questionnaire is completed

        // Update phase ref for footer
        if (phaseRef?.current) {
            Object.assign(phaseRef.current, { isPhase1: true });
        }
    };

    useImperativeHandle(ref, () => ({
        handleContinue,
        handleBack,
        isCompleting,
        isAdvancing,
        handleStepExit,
    }));

    const handleDocumentUpload = useCallback(
        (documentId: string) => {
            // Find the actual document from the generated documents
            const allDocuments = [
                // Business documents
                ...getDocumentTypesFromAnswers(questionnaireAnswers)
                    .filter((type) => type !== 'BUSINESS_BANK_STATEMENT')
                    .map((documentType, index) => ({
                        id: `business-doc-${index}`,
                        name: getDocumentTypeDisplayName(documentType),
                        description: getDocumentTypeDescription(documentType),
                        required: true,
                        documentType,
                    })),
                // Bank statement documents
                ...businessAccounts.map((account) => ({
                    id: `bank-statement-${account.id}`,
                    name: 'Bank Statement',
                    description: `Most recent bank statement for ${account.name} (${account.institution_name})`,
                    required: true,
                    documentType: 'BUSINESS_BANK_STATEMENT',
                    bankAccountId: account.id,
                    bankAccountName: `${account.name} (${account.institution_name})`,
                    institutionName: account.institution_name,
                    institutionId: account.institution_id,
                })),
            ];

            const document = allDocuments.find((doc) => doc.id === documentId);

            if (document) {
                // Track document upload started
                sendTrackingEvent('Document Upload Started', {
                    category: 'accounting_setup',
                    action: 'document_upload_started',
                    stepName: 'upload_documents',
                    documentType: document.documentType,
                    documentId,
                });

                setSelectedDocument(document);
                setUploadModalOpen(true);
            } else {
                // eslint-disable-next-line no-console
                console.error('Document not found:', documentId);
                enqueueSnackbar('Document not found. Please try again.', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            }
        },
        [questionnaireAnswers, businessAccounts, enqueueSnackbar, sendTrackingEvent]
    );

    const handleDocumentSkip = useCallback(
        (documentId: string, skipped: boolean) => {
            const newSkippedDocuments = {
                ...skippedDocuments,
                [documentId]: skipped,
            };

            setSkippedDocuments(newSkippedDocuments);

            // Track document skip
            if (skipped) {
                sendTrackingEvent('Document Skipped', {
                    category: 'accounting_setup',
                    action: 'document_skipped',
                    stepName: 'upload_documents',
                    documentId,
                    skippedDocuments: Object.keys(newSkippedDocuments).length,
                });
            }

            // Note: Not saving to backend until user is on this step
        },
        [skippedDocuments, sendTrackingEvent]
    );

    const handleModalSave = useCallback(
        async (documentId: string, uploadedDocument: File) => {
            // Scoped to the whole callback, not to the `if (documentType)` branch below: the
            // tracking event at the bottom reads it too.
            const newFiles = Array.isArray(uploadedDocument) ? uploadedDocument : [uploadedDocument];

            // Store the uploaded document data - append to existing files instead of overwriting
            setUploadedDocuments((prev) => {
                const existingFiles = prev[documentId] || [];

                return {
                    ...prev,
                    [documentId]: [...existingFiles, ...newFiles],
                };
            });

            // Find the document type for this document
            const allDocuments = [
                // Business documents
                ...getDocumentTypesFromAnswers(questionnaireAnswers)
                    .filter((type) => type !== 'BUSINESS_BANK_STATEMENT')
                    .map((documentType, index) => ({
                        id: `business-doc-${index}`,
                        documentType,
                    })),
                // Bank statement documents
                ...businessAccounts.map((account) => ({
                    id: `bank-statement-${account.id}`,
                    documentType: 'BUSINESS_BANK_STATEMENT',
                    bankAccountId: account.id,
                    bankAccountName: `${account.name} (${account.institution_name})`,
                })),
            ];

            const document = allDocuments.find((doc) => doc.id === documentId);
            const documentType = document?.documentType;

            if (documentType) {
                // Prepare document metadata for backend
                const metadataForFiles = newFiles.map((file: File) => ({
                    document_type: documentType,
                    // Through `unknown`: the parameter is typed `File`, but an already-uploaded
                    // document arrives here as `FileMetadata`, and the two do not overlap.
                    //
                    // `randomUUID` rather than `Math.random`, matching `useDecagonChat`: the id
                    // only has to be unique, but Sonar counts a PRNG as a security hotspot and the
                    // gate requires every hotspot on new code to be reviewed.
                    member_document_id:
                        (file as unknown as FileMetadata).id ||
                        `temp-${Date.now()}-${crypto.randomUUID().substring(0, 7)}`,
                    uploaded_at: new Date().toISOString(),
                    file_name: (file as any).file_name || file.name || 'Unknown',
                    // Include bank account information for bank statements
                    ...(documentType === 'BUSINESS_BANK_STATEMENT' && 'bankAccountId' in document
                        ? {
                              bank_account_id: (document as any).bankAccountId,
                              bank_account_name: (document as any).bankAccountName,
                          }
                        : {}),
                }));

                documentMetadata.addDocuments(metadataForFiles, documentType, (error) => {
                    // eslint-disable-next-line no-console
                    console.error('Failed to save document metadata:', error);
                    enqueueSnackbar('Document uploaded but failed to save metadata. Please try again.', {
                        variant: 'error',
                        autoHideDuration: 5000,
                    });
                });
            }

            // Close modal and reset state
            setUploadModalOpen(false);
            setSelectedDocument(null);

            // Show success notification
            // Track document uploaded
            sendTrackingEvent('Document Uploaded', {
                category: 'accounting_setup',
                action: 'document_uploaded',
                stepName: 'upload_documents',
                documentType,
                documentId,
                // Read off the render's state rather than the updater's parameter, which is not in
                // scope here. Analytics only, so a count from this render is close enough.
                documentCount: Object.values(uploadedDocuments).flat().length + newFiles.length,
            });

            enqueueSnackbar('Document uploaded successfully!', {
                variant: 'success',
                autoHideDuration: 3000,
            });
        },
        [
            enqueueSnackbar,
            questionnaireAnswers,
            businessAccounts,
            documentMetadata,
            sendTrackingEvent,
            uploadedDocuments,
        ] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const closeCompleteConfirmModal = useCallback(() => setShowCompleteConfirmModal(false), []);

    const handleConfirmFinish = useCallback(async () => {
        await completeStepAndExit();
    }, [completeStepAndExit]);

    const handleModalCancel = useCallback(() => {
        setUploadModalOpen(false);
        setSelectedDocument(null);
    }, []);

    const handleDeleteFile = useCallback(
        async (documentId: string, fileId: string) => {
            try {
                // Delete from MemberDocument collection
                await deleteMemberDocumentMutation.mutateAsync(fileId);

                // Remove the specific file from the document's file list
                setUploadedDocuments((prev) => {
                    const existingFiles = prev[documentId] || [];
                    const updatedFiles = existingFiles.filter((file: File | FileMetadata) => {
                        // Handle both array format and direct object format
                        const fileObj = Array.isArray(file) ? file[0] : file;
                        // Check if it's a FileMetadata object (has id property) or a File object
                        const currentFileId = 'id' in fileObj ? fileObj.id : (fileObj as FileMetadata).id;
                        return currentFileId !== fileId;
                    });

                    return {
                        ...prev,
                        [documentId]: updatedFiles,
                    };
                });

                documentMetadata.removeDocument(fileId, (error) => {
                    // eslint-disable-next-line no-console
                    console.error('Failed to update backend metadata:', error);
                });

                // Show success notification
                enqueueSnackbar('File deleted successfully!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to delete file:', error);
                enqueueSnackbar('Failed to delete file. Please try again.', {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
            }
        },
        [enqueueSnackbar, deleteMemberDocumentMutation, documentMetadata]
    );

    // Initialize phase ref
    useEffect(() => {
        if (phaseRef?.current) {
            Object.assign(phaseRef.current, { isPhase1: showQuestionnaire });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showQuestionnaire, member?.usertype, member?.establisheddate, questionnaireAnswers]);

    // Check if step can proceed
    useEffect(() => {
        if (showQuestionnaire) {
            // In questionnaire phase - check if all relevant questions are answered
            // For S-Corp: check all questions including businessTaxReturn (only if formed before current tax year)
            // For LLC: skip businessTaxReturn validation
            const isSCorp = isSCorpMember(member?.usertype || '');
            const currentTaxYear = new Date().getFullYear();
            const isFormedInCurrentTaxYear = member?.establisheddate
                ? new Date(member.establisheddate).getFullYear() === currentTaxYear
                : false;
            const relevantAnswers = {
                individualTaxReturn: questionnaireAnswers.individualTaxReturn,
                businessVehicle: questionnaireAnswers.businessVehicle,
                businessLoans: questionnaireAnswers.businessLoans,
                accountingSoftware: questionnaireAnswers.accountingSoftware,
                // Only include businessTaxReturn for S-Corp members who were formed before current tax year
                ...(isSCorp &&
                    !isFormedInCurrentTaxYear && { businessTaxReturn: questionnaireAnswers.businessTaxReturn }),
            };

            const values = Object.values(relevantAnswers);
            const isQuestionnaireComplete = values.length > 0 && values.every((answer) => answer !== null);
            setCanProceedRef.current?.(isQuestionnaireComplete);
        } else {
            // In document upload phase - check if ALL required documents are uploaded or skipped
            const requiredDocuments = [
                // Business documents based on questionnaire answers
                ...getDocumentTypesFromAnswers(questionnaireAnswers)
                    .filter((type) => type !== 'BUSINESS_BANK_STATEMENT')
                    .map((documentType, index) => `business-doc-${index}`),
                // Bank statement documents. With verification on, statements are gated by
                // the section's own readiness (all months covered, or the account waived)
                // instead of by these entries, which only checked that a file existed.
                ...(isStatementVerificationEnabled || !includeStatements
                    ? []
                    : businessAccounts.map((account) => `bank-statement-${account.id}`)),
            ];

            // Check if all required documents are either uploaded or skipped
            const allDocumentsHandled = requiredDocuments.every(
                (docId) =>
                    (uploadedDocuments[docId] && uploadedDocuments[docId].length > 0) ||
                    skippedDocuments[docId] === true
            );

            // `areStatementsReady` is already true when the gate is off or statements are not
            // included, so no branch here.
            setCanProceedRef.current?.(allDocumentsHandled && areStatementsReady);
        }
    }, [
        showQuestionnaire,
        questionnaireAnswers,
        uploadedDocuments,
        skippedDocuments,
        businessAccounts,
        member?.usertype,
        member?.establisheddate,
        isStatementVerificationEnabled,
        includeStatements,
        areStatementsReady,
    ]);

    return (
        <Box
            sx={{
                maxWidth: {
                    xs: '100%',
                    sm: '560px',
                },
                paddingBottom: 4,
                width: '100%',
            }}>
            {showQuestionnaire ? (
                /* Phase 1: Questionnaire */
                <UploadDocumentsQuestionnaire
                    initialAnswers={questionnaireAnswers}
                    onAnswersChange={handleAnswersChange}
                    isSaving={updateStep.isPending}
                />
            ) : (
                /* Phase 2: Document Upload List */
                <>
                    {/* Header */}
                    <Box sx={{ textAlign: 'left', mb: 6 }}>
                        <Typography
                            variant="h1"
                            color="text.primary"
                            sx={{
                                mb: 1.5,
                            }}>
                            Your document checklist is ready
                        </Typography>
                        <Typography variant="body1">
                            Provide these records to keep your books accurate and ready for tax time. If anything&apos;s
                            missing, we&apos;ll reach out to help.
                        </Typography>
                        {/* Page-level rather than beside the statements header it used to sit in, so
                            the member meets it with the intro copy instead of part-way down the
                            checklist. Gated on the section actually rendering cards — see
                            useStatementReadiness's hasUploadCards. */}
                        {hasStatementsToUpload && (
                            <Box sx={{ mt: 1 }}>
                                <WhyImportantLink
                                    context={ACCOUNTING_SETUP_HELP_CONTEXT.statementUpload}
                                    title="Why do you need my bank statements?"
                                    size="medium"
                                    requiresSetupAgent
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Document Upload List */}
                    <DocumentUploadList
                        questionnaireAnswers={questionnaireAnswers}
                        businessAccounts={businessAccounts}
                        uploadedDocuments={uploadedDocuments}
                        skippedDocuments={skippedDocuments}
                        onUpload={handleDocumentUpload}
                        onSkip={handleDocumentSkip}
                        onDeleteFile={handleDeleteFile}
                        includeStatements={includeStatements}
                        appearance={isRedesignV1 ? 'redesign' : 'default'}
                    />

                    {/* Last in the step, so it sits beside the footer control that triggered the
                        completion — the member is looking at Continue when this appears. Gated:
                        with the gate off this same error is still reported inside the legacy
                        confirmation, and rendering it in both places would show it twice. */}
                    {isStatementVerificationEnabled && backendError && (
                        <Alert severity="error" sx={{ mt: 3 }}>
                            {backendError}
                        </Alert>
                    )}
                </>
            )}

            {/* Asked once, on Complete, for every account still owing months. */}
            {isConfirmingIncomplete && (
                <IncompleteStatementsDialog
                    open
                    onAddMore={() => setIsConfirmingIncomplete(false)}
                    onSubmitAnyway={handleSubmitIncomplete}
                    accountLabel={incompleteDialogLabels.accountLabel}
                    missingPeriods={incompleteDialogLabels.missingPeriods}
                />
            )}

            {/* Upload Modal */}
            <UploadDocumentModal
                open={uploadModalOpen}
                title={selectedDocument?.name || ''}
                documentType={selectedDocument?.documentType || ''}
                meta={{
                    category: 'Accounting Setup',
                    bank_account_id: selectedDocument?.bankAccountId,
                    bank_account_name: selectedDocument?.bankAccountName,
                }}
                onClose={() => setUploadModalOpen(false)}
                onSave={(uploadedFiles) => {
                    if (selectedDocument) {
                        handleModalSave(selectedDocument.id, uploadedFiles[0] as unknown as File);
                    }
                }}
                onCancel={handleModalCancel}
            />

            {isRedesignV1 && (
                <ConfirmationDialog
                    open={showCompleteConfirmModal}
                    handleClose={isCompleting ? undefined : closeCompleteConfirmModal}
                    title="Done uploading your documents?"
                    message={
                        <>
                            <Typography variant="body1" color="text.primary">
                                Make sure you&apos;ve uploaded all the documents you have available. We&apos;ll review
                                them and reach out if anything else is needed.
                            </Typography>
                            {backendError && <Alert severity="error">{backendError}</Alert>}
                        </>
                    }
                    reject={{
                        label: 'Go back',
                        variant: 'text',
                        onClick: closeCompleteConfirmModal,
                        disabled: isCompleting,
                    }}
                    accept={{ label: 'Finish', onClick: handleConfirmFinish, loading: isCompleting }}
                />
            )}

            {/* Legacy pre-completion confirmation, gate-off only. Kept verbatim so the legacy path
                is unchanged, and deleted with the rest of the legacy step when the gate retires.
                Confirm calls `completeStepAndExit` — the same write the gated path calls. */}
            {!isRedesignV1 && !isStatementVerificationEnabled && (
                <Dialog
                    open={showCompleteConfirmModal}
                    onClose={() => setShowCompleteConfirmModal(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            boxShadow: 3,
                        },
                    }}>
                    <DialogTitle
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            py: 3,
                            px: 3,
                        }}>
                        <Typography variant="h3">Confirm document upload</Typography>
                        <IconButton
                            aria-label="Close"
                            size="small"
                            onClick={() => setShowCompleteConfirmModal(false)}
                            sx={{ color: 'text.primary', position: 'absolute', top: 8, right: 8 }}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }} dividers>
                        <Typography variant="body1" color="text.primary">
                            Please confirm that you&apos;ve uploaded all required documents to the best of your ability.
                            <br />
                            <br />
                            Our team will use these documents to set up your bookkeeping and will review everything
                            shortly. We&apos;ll reach out to you directly if we need any additional information.
                        </Typography>

                        {/* Error display */}
                        {backendError && <Alert severity="error">{backendError}</Alert>}
                    </DialogContent>

                    <DialogActions sx={{ py: 2, px: 3, gap: 1 }}>
                        <Button
                            onClick={() => setShowCompleteConfirmModal(false)}
                            disabled={isCompleting}
                            variant="outlined"
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'cement.50', // light, neutral hover tint
                                    borderColor: (t) => t.palette.purple,
                                },
                            }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={completeStepAndExit}
                            disabled={isCompleting}
                            variant="contained"
                            startIcon={isCompleting ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ minWidth: 120 }}>
                            {isCompleting ? 'Processing...' : 'Confirm'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
});

UploadDocuments.displayName = 'UploadDocuments';

export default UploadDocuments;
